// Centralized utility for consistent user name display across the application

interface UserData {
  firstname?: string | null;
  lastname?: string | null;
  name?: string | null;
  email?: string;
}

/**
 * Get a unified display name for a user
 * Priority: firstname + lastname > name > email username
 */
export function getDisplayName(user: UserData): string {
  // Priority 1: firstname + lastname
  if (user.firstname && user.lastname) {
    return `${user.firstname.trim()} ${user.lastname.trim()}`.trim();
  }
  
  // Priority 2: firstname only
  if (user.firstname && user.firstname.trim() !== '') {
    return user.firstname.trim();
  }
  
  // Priority 3: lastname only
  if (user.lastname && user.lastname.trim() !== '') {
    return user.lastname.trim();
  }
  
  // Priority 4: name field
  if (user.name && user.name.trim() !== '') {
    return user.name.trim();
  }
  
  // Priority 5: email username (fallback)
  if (user.email) {
    return user.email.split('@')[0];
  }
  
  return 'Utilisateur';
}

/**
 * Get initials for avatar display
 */
export function getInitials(user: UserData): string {
  const displayName = getDisplayName(user);
  
  return displayName
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

/**
 * Format member role and company for display
 * Returns null if both are empty/undefined
 */
export function formatMemberRole(member: { role?: string | null; company?: string | null }): string | null {
  const role = member.role?.trim();
  const company = member.company?.trim();
  
  // If both are empty or undefined, return null
  if (!role && !company) {
    return null;
  }
  
  // If only role is available
  if (role && !company) {
    return role;
  }
  
  // If only company is available
  if (!role && company) {
    return company;
  }
  
  // If both are available
  return `${role} chez ${company}`;
}

/**
 * Format member role and company for display based on member type
 * Adapts the formatting to the professional context of each member type
 * Returns null if both are empty/undefined
 */
export function formatMemberRoleByType(member: { 
  role?: string | null; 
  company?: string | null;
  type?: string | null;
}): string | null {
  const role = member.role?.trim();
  const company = member.company?.trim();
  const type = member.type;
  
  // Si les deux champs sont vides
  if (!role && !company) {
    return null;
  }
  
  // Si seulement le rôle est disponible
  if (role && !company) {
    return role;
  }
  
  // Si seulement l'entreprise/institution est disponible
  if (!role && company) {
    return company;
  }
  
  // Si les deux sont disponibles, adapter selon le type
  switch (type) {
    case 'chercheur':
      return `${role} · ${company}`;
    
    case 'journaliste':
      return `${role} · ${company}`;
    
    case 'influenceur':
      return `${role} · ${company}`;
    
    case 'consultant':
      return company === 'Indépendant' || company === 'Personnel' 
        ? `${role} · Indépendant` 
        : `${role} chez ${company}`;
    
    case 'expert':
      return company === 'Indépendant' || company === 'Personnel'
        ? `${role} · Indépendant`
        : `${role} · ${company}`;
    
    case 'association':
      return `${role} · ${company}`;
    
    case 'gouvernement':
      return `${role} · ${company}`;
    
    // Pour entrepreneur, startup, investisseur : format classique
    case 'entrepreneur':
    case 'startup':
    case 'investisseur':
    default:
      return `${role} chez ${company}`;
  }
}

/**
 * Check if user data is complete (has both firstname and lastname)
 */
export function isUserDataComplete(user: UserData): boolean {
  return !!(user.firstname && user.lastname && 
            user.firstname.trim() !== '' && user.lastname.trim() !== '');
}